import { useState, useEffect } from 'react';
import { Route, Switch, Redirect } from 'wouter';
import { useViewTransitionLocation } from './hooks/useViewTransitionLocation';
import { Home } from './pages/Home';
import { CreatorStudio } from './pages/CreatorStudio'; // MyPage v2
import { GodMode } from './pages/Admin/GodMode'; // God Mode (Admin)
import { PostDetail } from './pages/PostDetail';
import { Ranking } from './pages/Ranking';
import { SpPurchase } from './pages/SpPurchase';
import { Portfolio } from './pages/Portfolio';
import { Market } from './pages/Market';
import { Salon, SalonNoteDetail, SalonShelf } from './pages/Salon';
import { Tokiwa, TokiwaDessin, TokiwaPromptRoom, TokiwaGallery } from './pages/Tokiwa';
import { Stage, StageScriptDetail, StageCastingDetail } from './pages/Stage';
import { BoardCommon, BoardSalon, BoardTokiwa, BoardStage, BoardThreadDetail } from './pages/Board';
import { TokiwaRooms, TokiwaRoom } from './pages/TokiwaRooms';
import { Guidelines } from './pages/Guidelines';
import { StageRooms, StageRoom } from './pages/StageRooms';
import { LoginModal } from './components/LoginModal';
import { OnboardingModal } from './components/OnboardingModal';
import { NotificationBell } from './components/NotificationBell';
import { Toaster } from './components/ui/toaster';
import { EffectRenderer } from './components/EffectRenderer';
import { RewardReveal } from './components/RewardReveal';
import { api } from './services/api';
import { useGamenics, GamenicsEvent } from './hooks/useGamenics';
import { Icon } from './components/icons/Icon';
import { Footer } from './components/Footer';
import { MobileTabBar } from './components/MobileTabBar';
import { COPY } from './lib/copyDictionary';
import { BrandMark } from './components/BrandMark';
import { AudioProvider } from './hooks/useAudio';
import { GlobalPlayer } from './components/GlobalPlayer';
import { isAdmin } from './lib/permissions';

import clsx from 'clsx';

/**
 * 見つからないページ（一般的な文言）。
 * 管理画面のガードにも使うため、「管理者専用」等の"存在を示唆する文言"は置かない
 * （権限の無い人には、そのURLに何かがあること自体を伝えない）。
 */
const NotFoundView = () => {
    const [, navigate] = useViewTransitionLocation();
    return (
        <div className="max-w-[720px] mx-auto px-6 py-24 text-center">
            <h1 className="font-display font-black text-ink text-[clamp(24px,4vw,36px)] mb-3">ページが見つかりません</h1>
            <p className="text-ink-2 text-sm mb-8">お探しのページは移動したか、存在しないようです。</p>
            <button className="btn btn-primary min-h-[44px]" onClick={() => navigate('/')}>
                ホームへ戻る
            </button>
        </div>
    );
};

/**
 * 管理画面のルートガード（フロント側の表示制御）。
 * - セッション確認中: 何も描画しない（ガワの一瞬の露出とちらつきを防ぐ）
 * - 未ログイン / 非管理者: 404相当にフォールバック（管理画面の存在・構成を推測させない）
 * - 管理者のみ children を描画
 * ※ 認可の本体はサーバ側（authMiddleware + adminMiddleware）。ここはあくまで多層防御の一層。
 */
const AdminRoute = ({ user, sessionChecked, children }: { user: any; sessionChecked: boolean; children: React.ReactNode }) => {
    if (!sessionChecked) return null;
    if (!isAdmin(user)) return <NotFoundView />;
    return <>{children}</>;
};

function App() {
    const [location, setLocation] = useViewTransitionLocation();
    const [user, setUser] = useState<any>(null);
    // セッション確認が完了したか（未完了の間は権限依存UIを描画せず、ちらつき・一瞬の露出を防ぐ）
    const [sessionChecked, setSessionChecked] = useState(false);
    const [stats, setStats] = useState<any>(null);
    const [showLogin, setShowLogin] = useState(false);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const { playSound } = useGamenics();



    useEffect(() => {
        // Check session via API (Cookie)
        const checkSession = async () => {
            const result = await api.getUserProfile();
            if (result.success && result.data?.user) {
                const userData = result.data.user;
                setUser(userData);
                setStats(result.data.stats);
                if (!userData.isOnboarded) setShowOnboarding(true);
            } else {
                // Fallback to localStorage if API fails (e.g. offline?) or clear it
                const stored = localStorage.getItem('user');
                if (stored) {
                    try {
                        // 権限フラグはサーバ確認済みの値だけを信頼する。
                        // localStorage は古い flags を保持しうるため、復元時は権限を落とす
                        // （UIの出し分けが実権限より緩くならないようにする。認可の本体はサーバ側）。
                        const { flags: _staleFlags, ...restored } = JSON.parse(stored) ?? {};
                        setUser({ ...restored, flags: 0 });
                        // Re-verify in background?
                        // For now, if API says no, we should probably trust API (Cookie expired).
                        // But to be safe vs flakes, we keep local storage logic OR remove it.
                        // Better to rely on API for Real Auth.
                        if (!result.success) {
                            localStorage.removeItem('user');
                            setUser(null);
                        }
                    } catch (e) {
                        setUser(null);
                    }
                }
            }
            setSessionChecked(true);
        };
        checkSession();
    }, []);

    useEffect(() => {
        // [C1] If we are on /auth/callback, it means we probably just finished a redirect.
        // We should clear the URL state to prevent accidental loops.
        if (location.includes('/auth/callback')) {
            console.log('[Auth] Detected callback URL, redirecting to home...');
            setLocation('/');
        }
    }, [location]);

    const handleLoginSuccess = (data: any) => {
        setUser(data.user);
        setStats(data.stats);
        if (!data.user.isOnboarded) setShowOnboarding(true);
        localStorage.setItem('user', JSON.stringify(data.user));
    };

    const handleLogout = () => {
        setUser(null);
        setStats(null);
        localStorage.removeItem('user');
    };



    console.log('App: Rendering, location:', location);

    return (
        <AudioProvider>
        <div className="min-h-screen bg-cream text-ink font-sans selection:bg-primary selection:text-white pb-16">
            {/* a11y: キーボード利用者向けスキップリンク（フォーカス時のみ表示） */}
            <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] btn btn-primary">
                メインコンテンツへスキップ
            </a>
            <header className="sticky top-0 w-full px-6 py-3 flex justify-between items-center z-50 bg-cream/85 backdrop-blur-md border-b-2 border-ink">
                <div
                    onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation('/'); }}
                    className="cursor-pointer flex items-center gap-2"
                >
                    {/* 画像スロット: Admin登録ロゴ（未登録時は3役色の既定マーク） */}
                    <BrandMark />
                    <h1 className="text-2xl font-display font-black tracking-wide text-primary">
                        ANOKOE
                    </h1>
                </div>
                {/* ナビリンクは md 以上のみ（モバイルは下部タブバーが担う）。認証系はモバイルでも常時表示 */}
                <div className="flex items-center gap-3">
                <nav className="hidden md:flex gap-4 text-sm font-medium text-ink-2 items-center">
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation('/ranking'); }}
                    >
                        <Icon name="star" size={16} /> ランキング
                    </button>
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation('/'); setTimeout(() => document.getElementById('themes')?.scrollIntoView({ behavior: 'smooth' }), 100); }}
                    >
                        <Icon name="search" size={16} /> {COPY.themeList}
                    </button>
                    {/* 掲示板（2026-08-14 導線強化: 実装済みなのに入口がホームのカード1枚だけだった） */}
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation('/board'); }}
                    >
                        <Icon name="message" size={16} /> 掲示板
                    </button>
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation('/market'); }}
                    >
                        <Icon name="star" size={16} /> マーケット
                    </button>
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation('/sp'); }}
                    >
                        <Icon name="gift" size={16} /> SP購入
                    </button>
                </nav>

                    {user ? (
                        <div className="flex items-center gap-4">
                            {/* Economy Stats */}
                            <div className="hidden sm:flex items-center gap-3 px-3 py-1.5 bg-surface border-2 border-ink rounded-full text-xs font-bold">
                                <div className="flex items-center gap-1.5 text-ink">
                                    <span className="w-2.5 h-2.5 rounded-full bg-sun border-2 border-ink" />
                                    <span>{stats?.adp || 0} ADP</span>
                                </div>
                                <div className="w-px h-3 bg-ink/20" />
                                <div className="flex items-center gap-1.5 text-ink">
                                    <span className="w-2.5 h-2.5 rounded-full bg-pop border-2 border-ink" />
                                    <span>{user.streakCount || 0}</span>
                                </div>
                            </div>

                            <NotificationBell />

                            <div className="relative">
                                <button
                                    onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setDropdownOpen(!dropdownOpen); }}
                                    className="flex items-center gap-2 transition-colors focus:outline-none relative z-50 py-1.5 px-3 rounded-full hover:bg-surface"
                                >
                                    <img src={user.iconUrl || user.icon || `https://ui-avatars.com/api/?name=${user.displayName || user.name}`} alt="User" className="w-8 h-8 rounded-full border-2 border-ink" />
                                    <span className="text-sm font-medium">{user.displayName || user.name}</span>
                                </button>

                                {/* Backdrop to close on click outside */}
                                {dropdownOpen && (
                                    <div
                                        className="fixed inset-0 z-40 cursor-default"
                                        onClick={() => setDropdownOpen(false)}
                                    />
                                )}

                                {/* Dropdown */}
                                <div className={clsx(
                                    "absolute right-0 mt-2 w-48 bg-surface border-2 border-ink rounded-pop-md shadow-pop-sm transition-all duration-200 z-50 flex flex-col overflow-hidden",
                                    dropdownOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-2 pointer-events-none"
                                )}>
                                    <button onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation('/mypage'); setDropdownOpen(false); }} className="text-left px-4 py-3 text-ink hover:bg-cream border-b-2 border-cream-2 text-sm font-bold">
                                        {COPY.mypage}
                                    </button>
                                    <button onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation('/mypage/portfolio'); setDropdownOpen(false); }} className="text-left px-4 py-3 text-ink hover:bg-cream border-b-2 border-cream-2 text-sm font-bold">
                                        ポートフォリオ
                                    </button>
                                    {/* 管理リンクは管理者にのみ表示（非管理者には導線も見せない） */}
                                    {isAdmin(user) && (
                                        <button onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation('/admin'); setDropdownOpen(false); }} className="text-left px-4 py-3 text-primary hover:bg-cream font-mono text-[10px] border-b-2 border-cream-2 opacity-60 hover:opacity-100">
                                            {COPY.admin}
                                        </button>
                                    )}
                                    <button onClick={() => { playSound(GamenicsEvent.TAP_CANCEL); handleLogout(); }} className="text-left px-4 py-3 text-red-500/80 hover:text-red-500 hover:bg-cream text-sm font-bold transition-colors">
                                        {COPY.logout}
                                    </button>
                                </div>
                            </div>
                        </div>

                    ) : (
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setShowLogin(true); }}
                        >
                            <Icon name="user" size={16} /> {COPY.login}
                        </button>
                    )}
                </div>
            </header>

            <main id="main">
                <Switch>
                    <Route path="/auth/callback">
                        <Redirect to="/" />
                    </Route>
                    <Route path="/" component={Home} />
                    {/* 三役の街（Phase 1「開街」）— 3街区同時 */}
                    <Route path="/salon" component={Salon} />
                    <Route path="/salon/notes/:id" component={SalonNoteDetail} />
                    <Route path="/salon/shelves/:userId" component={SalonShelf} />
                    <Route path="/tokiwa" component={Tokiwa} />
                    <Route path="/tokiwa/dessin" component={TokiwaDessin} />
                    <Route path="/tokiwa/prompt" component={TokiwaPromptRoom} />
                    <Route path="/tokiwa/gallery" component={TokiwaGallery} />
                    {/* Phase 3-A: RT-05 共有キャンバス */}
                    <Route path="/tokiwa/rooms" component={TokiwaRooms} />
                    <Route path="/tokiwa/rooms/:id" component={TokiwaRoom} />
                    <Route path="/stage" component={Stage} />
                    {/* 稽古場（RT-01 / B-1）。:id より先に一覧を置く */}
                    <Route path="/stage/rooms" component={StageRooms} />
                    <Route path="/stage/rooms/:id" component={StageRoom} />
                    <Route path="/stage/scripts/:id" component={StageScriptDetail} />
                    <Route path="/stage/castings/:id" component={StageCastingDetail} />
                    {/* 掲示板（BBS）: 共通1つ＋各街区1つ（決定22） */}
                    <Route path="/board" component={BoardCommon} />
                    <Route path="/board/threads/:id" component={BoardThreadDetail} />
                    <Route path="/salon/board" component={BoardSalon} />
                    <Route path="/tokiwa/board" component={BoardTokiwa} />
                    <Route path="/stage/board" component={BoardStage} />
                    <Route path="/post/:id" component={PostDetail} />
                    <Route path="/ranking" component={Ranking} />
                    <Route path="/guidelines" component={Guidelines} />
                    <Route path="/market" component={Market} />
                    <Route path="/sp/success" component={SpPurchase} />
                    <Route path="/sp" component={SpPurchase} />
                    <Route path="/themes/:id">
                        <Redirect to="/" />
                    </Route>
                    <Route path="/mypage/portfolio" component={Portfolio} />
                    <Route path="/mypage" component={CreatorStudio} />
                    {/* 管理画面は管理者のみ。非管理者・未ログインには404相当を返す（存在を示唆しない） */}
                    <Route path="/admin">
                        <AdminRoute user={user} sessionChecked={sessionChecked}>
                            <GodMode />
                        </AdminRoute>
                    </Route>
                    <Route component={Home} />
                </Switch>
            </main>

            <Footer />

            {
                showLogin && (
                    <LoginModal
                        onClose={() => setShowLogin(false)}
                        onLoginSuccess={handleLoginSuccess}
                    />
                )
            }
            <OnboardingModal
                isOpen={showOnboarding}
                initialName={user?.displayName || ''}
                onComplete={(updatedFields) => {
                    setUser({ ...user, ...updatedFields });
                    setShowOnboarding(false);
                    // Also update localStorage if needed
                    localStorage.setItem('user', JSON.stringify({ ...user, ...updatedFields }));
                }}
            />
            <EffectRenderer />
            <RewardReveal />
            <Toaster />
            <GlobalPlayer />
            <MobileTabBar
                location={location}
                onNavigate={setLocation}
                isLoggedIn={!!user}
                onLoginClick={() => setShowLogin(true)}
            />
        </div>
        </AudioProvider>
    );
}

export default App;
